import React, { useEffect, useMemo, useState } from 'react';
import {
  buildFirebaseSessionId,
  sanitizeFirebaseKey,
  subscribeSessionUsers,
  usersAllByPuzzleId,
  usersByPuzzleId,
} from '../../utils/puzzleFirebase';
import LivePuzzlePiece from './LivePuzzlePiece';
import './LivePuzzleBoard.css';

export default function LivePuzzleBoard({
  sessionId,
  organizationId = null,
  sliceUrls = [],
  macroRows = 3,
  macroCols = 3,
  tileRows = 3,
  tileCols = 3,
  currentUserId = null,
  commaSeparateNames = false,
}) {
  const [usersMap, setUsersMap] = useState({});

  const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);
  const firebaseUserId = currentUserId ? sanitizeFirebaseKey(currentUserId) : null;

  useEffect(() => {
    if (!firebaseSessionId) return undefined;
    return subscribeSessionUsers(firebaseSessionId, setUsersMap);
  }, [firebaseSessionId]);

  const usersForPieces = useMemo(() => usersByPuzzleId(usersMap), [usersMap]);
  const allUsersForPieces = useMemo(() => usersAllByPuzzleId(usersMap), [usersMap]);
  const myState = firebaseUserId ? usersMap[firebaseUserId] : null;
  const myPieceId = myState
    ? Math.floor(Number(myState.puzzle_id))
    : null;
  const totalParts = sliceUrls.length;

  if (!totalParts) {
    return (
      <aside className="live-puzzle-board" aria-label="Live team puzzle">
        <p className="live-puzzle-board-empty">Team puzzle preview unavailable</p>
      </aside>
    );
  }

  const gridLabel = `${macroRows} rows × ${macroCols} columns = ${totalParts} pieces`;

  return (
    <aside className="live-puzzle-board" aria-label="Live team puzzle">
      <div className="live-puzzle-board-card">
        <h2 className="live-puzzle-board-title">Team Big Picture</h2>
        <p className="live-puzzle-board-subtitle">Live solving — updates as teammates place pieces</p>
        <p className="live-puzzle-board-grid-label">{gridLabel}</p>
        <div
          className="live-puzzle-board-grid"
          style={{
            gridTemplateColumns: `repeat(${macroCols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${macroRows}, minmax(0, 1fr))`,
            aspectRatio: `${macroCols} / ${macroRows}`,
          }}
        >
          {sliceUrls.map((src, index) => {
            const pieceId = index + 1;
            const isMine = Number.isFinite(myPieceId) && myPieceId === pieceId;
            const userState =
              isMine && myState ? myState : usersForPieces[pieceId] || null;
            const playersOnPiece = allUsersForPieces[pieceId] || [];

            return (
              <LivePuzzlePiece
                key={`piece-${pieceId}`}
                src={src}
                pieceId={pieceId}
                userState={userState}
                playersOnPiece={playersOnPiece}
                tileRows={tileRows}
                tileCols={tileCols}
                isMine={Boolean(isMine)}
                commaSeparateNames={commaSeparateNames}
              />
            );
          })}
        </div>
      </div>
    </aside>
  );
}
